import { z } from 'zod';

import {
  EquipmentSchema,
  ExerciseCategorySchema,
  ExerciseTypeSchema,
  MuscleGroupSchema,
  SubMuscleGroupSchema,
} from './exercise.js';
import {
  FoodCategoryEnum,
  FoodIdSchema,
  FoodSizesSchema,
  FoodSourceRefEnum,
  FoodTypeEnum,
  FoodUnitTypeEnum,
} from './foods.js';

export const AdminRangeSchema = z.enum(['7d', '30d', '90d']).default('30d');
export type AdminRange = z.infer<typeof AdminRangeSchema>;

export const AdminOverviewQuerySchema = z.object({ range: AdminRangeSchema });
export type AdminOverviewQuery = z.infer<typeof AdminOverviewQuerySchema>;

export interface AdminMetric {
  value: number;
  changePercent: number | null;
}

export interface AdminOverviewResponse {
  range: AdminRange;
  generatedAt: string;
  metrics: {
    totalUsers: AdminMetric;
    activeUsers: AdminMetric;
    newUsers: AdminMetric;
    completedOnboarding: AdminMetric;
  };
  engagement: {
    workoutUsers: number;
    nutritionUsers: number;
    weightLogUsers: number;
    reviewUsers: number;
    activePlans: number;
  };
  activityTrend: Array<{
    date: string;
    signups: number;
    workouts: number;
    foodLogs: number;
    weightLogs: number;
  }>;
  recentActivity: Array<{
    id: string;
    kind: 'signup' | 'workout' | 'food_log' | 'weight_log' | 'review';
    userId: string;
    userName: string | null;
    userEmail: string;
    occurredAt: string;
    detail: string;
  }>;
  catalogs: { foods: number; exercises: number; archivedFoods: number; archivedExercises: number };
}

export const AdminUsersQuerySchema = z.object({
  q: z.string().trim().max(120).optional(),
  status: z.enum(['all', 'active', 'inactive', 'onboarded']).default('all'),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(25),
});
export type AdminUsersQuery = z.infer<typeof AdminUsersQuerySchema>;

export interface AdminUserSummary {
  id: string;
  name: string | null;
  email: string;
  avatarUrl: string | null;
  role: 'USER' | 'ADMIN';
  createdAt: string;
  lastActiveAt: string | null;
  onboardingComplete: boolean;
  trackingModes: string[];
  counts: { workouts: number; foodLogs: number; weightLogs: number; reviews: number };
}

export interface AdminUsersResponse {
  items: AdminUserSummary[];
  page: number;
  limit: number;
  total: number;
}

export interface AdminUserDetailResponse extends AdminUserSummary {
  goal: {
    type: string | null;
    currentWeightKg: number | null;
    targetWeightKg: number | null;
    targetDate: string | null;
  };
  activePlans: Array<{ id: string; type: string; startsOn: string; endsOn: string | null }>;
  latestWeight: { weightKg: number; loggedAt: string } | null;
  joinedAt: string;
}

export const AdminCatalogQuerySchema = z.object({
  q: z.string().trim().max(120).optional(),
  status: z.enum(['active', 'archived', 'all']).default('active'),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(25),
});
export type AdminCatalogQuery = z.infer<typeof AdminCatalogQuerySchema>;

const FoodWriteFields = z.object({
  id: FoodIdSchema,
  name: z.string().trim().min(1).max(120),
  category: FoodCategoryEnum,
  type: FoodTypeEnum,
  kcalPer100g: z.number().min(0).max(900),
  proteinPer100g: z.number().min(0).max(100),
  carbsPer100g: z.number().min(0).max(100),
  fatPer100g: z.number().min(0).max(100),
  defaultServingG: z.number().positive().max(2000),
  defaultServingLabel: z.string().trim().min(1).max(60),
  unitType: FoodUnitTypeEnum,
  sizes: FoodSizesSchema.nullable(),
  defaultGramsPerUnit: z.number().positive().nullable(),
  sourceRef: FoodSourceRefEnum.nullable(),
  sourceCode: z.string().trim().max(64).nullable(),
});

function validateFoodUnits(
  value: {
    unitType: z.infer<typeof FoodUnitTypeEnum>;
    sizes: z.infer<typeof FoodSizesSchema> | null;
    defaultGramsPerUnit: number | null;
  },
  ctx: z.RefinementCtx,
): void {
  if (value.unitType === 'count' && value.defaultGramsPerUnit === null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['defaultGramsPerUnit'],
      message: 'Count foods require grams per unit',
    });
  }
  if (value.unitType === 'portion' && value.sizes === null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['sizes'],
      message: 'Portion foods require small, medium and large sizes',
    });
  }
  if (value.unitType === 'grams' && (value.sizes !== null || value.defaultGramsPerUnit !== null)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['unitType'],
      message: 'Gram foods cannot define count or portion sizes',
    });
  }
}

export const AdminCreateFoodSchema = FoodWriteFields.superRefine(validateFoodUnits);
export const AdminUpdateFoodSchema = FoodWriteFields.omit({ id: true }).superRefine(
  validateFoodUnits,
);
export type AdminCreateFoodInput = z.infer<typeof AdminCreateFoodSchema>;
export type AdminUpdateFoodInput = z.infer<typeof AdminUpdateFoodSchema>;

export const AdminExerciseWriteSchema = z.object({
  id: z
    .string()
    .trim()
    .min(1)
    .max(128)
    .regex(/^[a-z][a-z0-9_]*$/),
  name: z.string().trim().min(1).max(200),
  primaryMuscle: MuscleGroupSchema,
  secondaryMuscles: z.array(MuscleGroupSchema).default([]),
  equipment: EquipmentSchema,
  type: ExerciseTypeSchema,
  category: ExerciseCategorySchema,
  instructions: z.array(z.string().trim().min(1).max(1000)).max(10),
  subMuscleGroup: SubMuscleGroupSchema,
});
export const AdminCreateExerciseSchema = AdminExerciseWriteSchema;
export const AdminUpdateExerciseSchema = AdminExerciseWriteSchema.omit({ id: true });
export type AdminCreateExerciseInput = z.infer<typeof AdminCreateExerciseSchema>;
export type AdminUpdateExerciseInput = z.infer<typeof AdminUpdateExerciseSchema>;

export interface AdminCatalogResponse<T> {
  items: T[];
  page: number;
  limit: number;
  total: number;
}

export interface AdminFoodItem extends AdminCreateFoodInput {
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AdminExerciseItem extends AdminCreateExerciseInput {
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AdminAuditItem {
  id: string;
  action: string;
  entityType: string;
  entityId: string;
  actor: { id: string; name: string | null; email: string };
  createdAt: string;
}
